import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/useI18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import API_CONFIG from '../config/apiConfig';

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, refresh: authRefresh } = useAuth();

  // 如果用户已经登录，重定向到返回地址或主页
  useEffect(() => {
    if (user) {
      const fromPath = location.state?.from?.pathname;
      const urlParam = new URLSearchParams(location.search).get('return_url');

      let returnUrl = '/';
      if (fromPath && !fromPath.includes('/website/')) {
        returnUrl = fromPath;
      } else if (urlParam && !urlParam.includes('/website/')) {
        returnUrl = urlParam;
      }

      const targetUrl = (returnUrl === '/login' || returnUrl.includes('/login')) ? '/' : returnUrl;
      navigate(targetUrl, { replace: true });
    }
  }, [user, location.state, navigate]);

  const getReturnUrl = () => {
    const fromPath = location.state?.from?.pathname;
    if (fromPath && !fromPath.includes('/website/')) {
      return fromPath;
    }
    const urlParam = new URLSearchParams(location.search).get('return_url');
    if (urlParam && !urlParam.includes('/website/')) {
      return urlParam;
    }
    return '/';
  };
  const returnUrl = getReturnUrl();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/auth/login.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        await authRefresh();
        navigate(returnUrl, { replace: true });
      } else {
        setError(result.message || t('loginFailed'));
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full" style={{ maxWidth: '400px' }}>
        <div className="text-center mb-6">
          {/* 登录图标 */}
          <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
            <span className="text-3xl">🔑</span>
          </div>
          {/* 语言选择行：地球图标 + 语言切换器 */}
          <div className="flex items-center justify-center gap-1 mb-3">
            <span className="text-xl">🌐</span>
            <LanguageSwitcher variant="light" />
          </div>
          {/* 主标题 */}
          <h1 className="text-xl font-bold text-gray-800">{t('loginTitle')}</h1>
          <p className="text-gray-500 text-xs mt-1">{t('loginSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('enterEmail')}
              autoComplete="email"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('enterPassword')}
              autoComplete="current-password"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-100 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('loggingIn') : t('loginButton')}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            {t('noAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-indigo-600 font-bold hover:underline"
            >
              {t('registerNow')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}